import { useState, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useScrollRestoration } from "./hooks/useScrollRestoration";
import styled from "styled-components";
import { AuthProvider } from "./components/auth/AuthProvider";
import { Footer } from "./components/common/Footer";
import { Header } from "./components/common/Header";
import { Sidebar } from "./components/common/Sidebar";
import { useAuthStore } from "./stores/auth.store";
import {
  SHOP_CART_STORAGE_KEY,
  useShopCartStore,
} from "./stores/shopCart.store";
import { AddToHomescreen } from "./components/AddToHomescreen";
import { NotFound } from "./pages/404";
import { Account } from "./pages/Account";
import { ChangePassword } from "./pages/ChangePassword";
import { Checkout } from "./pages/Checkout";
import { ConfirmCheckout } from "./pages/ConfirmCheckout";
import { ConfirmRegistration } from "./pages/ConfirmRegistration";
import { EditAccount } from "./pages/EditAccount";
import { EditMember } from "./pages/EditMember";
import { FamilyMembers } from "./pages/FamilyMembers";
import { Home } from "./pages/Home";
import { Login } from "./pages/Login";
import { MembershipSubscription } from "./pages/MemberSubscription";
import { PasswordRecover } from "./pages/PasswordRecover";
import { Register } from "./pages/Register";
import { CardDisplay } from "./pages/CardDisplay";
import { Conventions } from "./pages/Conventions";
import { Documents } from "./pages/Documents";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { AuthRoutes } from "./routes/AuthRoutes";
import { ExperimentalRoute } from "./routes/ExperimentalRoute";
import { ProtectedRoutes } from "./routes/ProtectedRoutes";
import { GlobalStyles } from "./styles/GlobalStyles";
import { CourseCheckout } from "./pages/courses/CourseCheckout";
import { CourseConfirm } from "./pages/courses/CourseConfirm";
import { CourseDetails } from "./pages/courses/CourseDetails";
import { Courses } from "./pages/courses/Courses";
import { CoursesLayout } from "./pages/courses/CoursesLayout";
import { SingleCourseSubscription } from "./pages/courses/SingleCourseSubscription";
import { Cart } from "./pages/shop/Cart";
import { Shop } from "./pages/shop/Shop";
import { ProductDetail } from "./pages/shop/ProductDetail";
import { ShopConfirm } from "./pages/shop/ShopConfirm";
import { ShopLayout } from "./pages/shop/ShopLayout";
import { ShopOrders } from "./pages/shop/ShopOrders";

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  margin-top: 60px;
  padding: 20px;
  background: white;
`;

// Component to normalize paths with double slashes
const PathNormalizer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if the pathname has double slashes
    if (location.pathname.includes('//')) {
      // Normalize the path by replacing multiple slashes with a single slash
      const normalizedPath = location.pathname.replace(/\/+/g, '/');
      // Preserve hash and search params
      const newPath = `${normalizedPath}${location.search}${location.hash}`;
      navigate(newPath, { replace: true });
    }
  }, [location, navigate]);

  return null;
};

const AppContent = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const authUser = useAuthStore((state) => state.authUser);
  const { initialize: initializeCart, syncFromStorage } = useShopCartStore();
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Handle scroll restoration for iOS Safari compatibility
  useScrollRestoration();

  useEffect(() => {
    // Restore the browser-local shop cart only after the app knows there is
    // an authenticated user for this session.
    if (!authUser) {
      return;
    }

    void initializeCart();
  }, [authUser, initializeCart]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== SHOP_CART_STORAGE_KEY) {
        return;
      }

      if (!useAuthStore.getState().authUser) {
        return;
      }

      void syncFromStorage();
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [syncFromStorage]);

  return (
    <AuthProvider>
      <AppContainer>
        <AddToHomescreen />
        <PathNormalizer />
        <Header onMenuClick={toggleSidebar} isOpen={sidebarOpen} />
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <MainContent>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/conventions" element={<Conventions />} />
            <Route element={<ShopLayout />}>
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/cart" element={<Cart />} />
              <Route path="/shop/confirm" element={<ShopConfirm />} />
              <Route path="/shop/:productId" element={<ProductDetail />} />
            </Route>
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/confirm-registration"
              element={<ConfirmRegistration />}
            />

            {/* Auth Routes */}
            <Route element={<AuthRoutes />}>
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              <Route
                path="/forgot-password"
                element={<PasswordRecover />}
              />
            </Route>

            {/* Authenticated user routes */}
            <Route element={<ProtectedRoutes />}>
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/account" element={<Account />} />
              <Route path="/account/members" element={<FamilyMembers />} />
              <Route path="/account/members/:id" element={<EditMember />} />
              <Route
                path="/account/membership"
                element={<MembershipSubscription />}
              />
              <Route path="/account/edit" element={<EditAccount />} />
              <Route path="/account/card" element={<CardDisplay />} />
              <Route path="/account/documents" element={<Documents />} />
              <Route path="/account/shop-orders" element={<ShopOrders />} />
              <Route
                path="/account/shop-orders/:orderId"
                element={<ShopOrders />}
              />
              <Route path="/checkout" element={<Checkout />} />
              <Route
                path="/checkout-confirm"
                element={<ConfirmCheckout />}
              />
              <Route element={<ExperimentalRoute feature="courses" />}>
                <Route path="/account/courses/info" element={<CourseDetails />} />
                <Route path="/account/courses" element={<CoursesLayout />}>
                  <Route index element={<Courses />} />
                  <Route path=":memberId" element={<SingleCourseSubscription />} />
                  <Route path="checkout" element={<CourseCheckout />} />
                  <Route path="confirm" element={<CourseConfirm />} />
                </Route>
              </Route>
            </Route>

            {/* 404 route - this should always be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </MainContent>
        <Footer />
      </AppContainer>
    </AuthProvider>
  );
};

const App = () => {

  return (
    <>
      <GlobalStyles />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </>
  );
};

export default App;
